import React, { useState } from 'react';
import { SimpleGrid } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import type { UseFormReturnType } from '@mantine/form';
import { IconLock } from '@tabler/icons-react';

import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { TextInput, Autocomplete, PhoneInput, Select } from '~/domains/design-system/TextInput';
import { Text } from '~/domains/design-system/Text';
import { Countries } from '~/common/constants/countries.enum';
import dt from '~/testing';
import ConfirmationModal from '~/domains/auth/components/ConfirmationModal/ConfirmationModal';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import { KYCDetailsMode } from '../types';
import type { KYCFormValues } from '../types';
import { useKYCDetailsForm } from '../utils/useKYCDetailsForm';

import useStyles from './styles';
import { SignUpContinueButton } from './SignUpContinueButton';

const dtIds = dt.components.shared.auth.signUpVersionTwo.kycDetailsForm;

function getCountryLabel(
  country: string,
  t: (key: string) => string,
  field: 'state' | 'zip'
): string {
  const suffix = country === Countries.US ? 'US' : 'CA';
  return t(`signUp.${field}.label.${suffix}`);
}

interface KYCDetailsFormProps {
  form: UseFormReturnType<KYCFormValues>;
  onClick: () => void;
  mode?: KYCDetailsMode;
  isRestrictedDomain?: boolean;
  isLoading?: boolean;
}

export function KYCDetailsForm({
  form,
  onClick,
  mode = KYCDetailsMode.MANUAL,
  isRestrictedDomain,
  isLoading,
}: KYCDetailsFormProps) {
  const { t } = useTranslation('auth');
  const { classes, theme } = useStyles();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    formFieldChange,
    stateFieldChange,
    countrySelectData,
    isButtonDisabled,
    dataForAutocomplete,
    handleAutoCompleteSubmit,
    handleSubmit,
    handleKeyDown,
    header,
  } = useKYCDetailsForm({ form, onClick, mode });

  if (isRestrictedDomain) {
    return null;
  }

  return (
    <>
      <div className={classes.headerContainer}>
        <SignUpHeader title={header.title} copy={header.copy} isLogoVisible={false} />
      </div>

      <div className={classes.formContainer}>
        <TextInput
          data-test-id={dtIds.firstNameInput}
          className={classes.textInput}
          label={t('signUp.firstName.label')}
          w="100%"
          onKeyDown={handleKeyDown}
          {...form.getInputProps('firstName')}
        />

        <TextInput
          data-test-id={dtIds.lastNameInput}
          className={classes.textInput}
          label={t('signUp.lastName.label')}
          w="100%"
          onKeyDown={handleKeyDown}
          {...form.getInputProps('lastName')}
        />

        <div className={classes.kycStartFieldGroup}>
          <Text variant="subhead-medium" fw={600} c="gray2.6">
            {t('signUp.headers.kycDetails.dobHeading')}
          </Text>
          <SimpleGrid
            spacing="md"
            cols={3}
            breakpoints={[
              { maxWidth: theme.breakpoints.md, cols: 3 },
              { maxWidth: theme.breakpoints.sm, cols: 3 },
            ]}
          >
            <TextInput
              data-test-id={dtIds.monthField}
              className={classes.textInput}
              label={t('signUp.dob.month.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.month', { type: 'input' })}
              onChange={(e) => formFieldChange('dob.month', e)}
            />
            <TextInput
              data-test-id={dtIds.dateField}
              className={classes.textInput}
              label={t('signUp.dob.day.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.day')}
              onChange={(e) => formFieldChange('dob.day', e)}
            />
            <TextInput
              data-test-id={dtIds.yearField}
              className={classes.textInput}
              label={t('signUp.dob.year.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.year')}
              onChange={(e) => formFieldChange('dob.year', e)}
            />
          </SimpleGrid>
        </div>

        <div className={classes.kycStartFieldGroup}>
          <Text variant="subhead-medium" fw={600} c="gray2.6">
            {t('signUp.headers.kycDetails.addressHeading')}
          </Text>

          <Select
            data-test-id={dtIds.countryInput}
            {...form.getInputProps('country')}
            label={t('signUp.country.label')}
            data={countrySelectData}
            className={classes.textInput}
            w="100%"
            pos="relative"
            onChange={(val) => {
              form.getInputProps('country').onChange(val);
              form.setFieldValue('state', form.values.state);
              form.setFieldValue('zip', form.values.zip);
            }}
          />

          <Autocomplete
            data-test-id={dtIds.addressInput}
            className={classes.textInput}
            {...form.getInputProps('address')}
            pos="relative"
            onItemSubmit={handleAutoCompleteSubmit}
            label={t('signUp.address.label')}
            data={dataForAutocomplete}
            maxLength={130}
            autoComplete="chrome-off"
            w="100%"
            onKeyDown={handleKeyDown}
          />

          <TextInput
            data-test-id={dtIds.cityInput}
            className={classes.textInput}
            label={t('signUp.city.label')}
            w="100%"
            onKeyDown={handleKeyDown}
            {...form.getInputProps('city')}
          />

          <SimpleGrid
            spacing="md"
            cols={2}
            sx={{ width: '100%' }}
            breakpoints={[
              { maxWidth: theme.breakpoints.md, cols: 2 },
              { maxWidth: theme.breakpoints.sm, cols: 2 },
            ]}
          >
            <TextInput
              data-test-id={dtIds.stateInput}
              className={classes.textInput}
              maxLength={2}
              label={getCountryLabel(form.values.country, t, 'state')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('state')}
              onChange={stateFieldChange}
            />
            <TextInput
              data-test-id={dtIds.zipInput}
              className={classes.textInput}
              maxLength={30}
              label={getCountryLabel(form.values.country, t, 'zip')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('zip')}
            />
          </SimpleGrid>
        </div>

        <div className={classes.kycStartFieldGroup}>
          <Text variant="subhead-medium" fw={600} c="gray2.6">
            {t('signUp.headers.kycDetails.phoneHeading')}
          </Text>
          <PhoneInput
            data-test-id={dtIds.phoneInput}
            className={classes.textInput}
            prefix="+1"
            label={t('signUp.phone.label')}
            maxLength={10}
            w="100%"
            onKeyDown={handleKeyDown}
            {...form.getInputProps('phone')}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleSubmit}
      />

      <div className={classes.kycDetailsFooter}>
        <ConnectedRegistrationBonusBanner />

        <SignUpContinueButton
          onClick={() => setIsConfirmOpen(true)}
          disabled={isButtonDisabled || isLoading}
          loading={isLoading}
          buttonText={t('signUpActions.verifyAndContinue')}
          data-test-id={dtIds.continueButton}
        />

        <Text variant="body-small" c="gray2.3">
          {t('signUp.headers.kycDetails.disclaimer')}
        </Text>

        <div className={classes.confirmInfoSecurityNote}>
          <IconLock size={24} color={theme.colors.gray[5]} />
          <Text variant="body-small" c="gray2.4">
            {t('signUp.confirmInfoCard.securityNote')}{' '}
            <Text
              variant="body-small"
              c="blue.3"
              fw={700}
              component="a"
              href="mailto:support@underdogfantasy.com"
              style={{ textDecoration: 'none' }}
            >
              {t('signUp.confirmInfoCard.contactSupport')}
            </Text>
          </Text>
        </div>
      </div>
    </>
  );
}
